import { navigate, RouteComponentProps } from '@reach/router';
import { AppPath, PageResourceKey } from 'appConstants';
import { usePageTranslations } from 'helpers/usePageTranslations';
import { SignInTranslation } from 'pages/signIn/signInTypes';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthStatus, setAuthStatus } from 'reducers/auth';
import { selectCultureCode } from 'reducers/language';
import Page from '../../components/page/Page';
import { EmailVerification } from './EmailVerification';
import {
    AccountDetails,
    AuthType,
    RegisterStep,
    AccountTypeAndServicesPayload,
    SignUpFormData,
} from './models';
import { postEmailVerificationCode } from './RegisterApi';
import { RegistrationSuccessMessage } from './RegistrationSuccessMessage';
import { SelectAuthType } from './SelectAuthType';
import { TFASetup } from './TFASetup';
import { TFAVerification } from './TFAVerification';
import { SignUpForm, type SignUpFormProps } from './SignUpForm';
import { AccountTypeAndServices } from './AccountTypeAndServices';
import ErrorBoundary from 'components/page/ErrorBoundary';
import ErrorFallback from 'components/fallbacks/ErrorFallback';

export type RegisterInitialState = {
    stage: 'emailVerification' | 'tfa';
    email: string;
    password: string;
};

export const Register: React.FC<RouteComponentProps & { skipWaitlist?: boolean }> = ({
    location,
}) => {
    const [registrationStep, setRegistrationStep] = useState<RegisterStep>(RegisterStep.SIGN_UP);
    const [tfaSetupSubStep, setTfaSetupSubStep] = useState<'select' | 'setup'>('select');
    const [authType, setAuthType] = useState<AuthType>(AuthType.APP);
    const [accountDetails, setAccountDetails] = useState<AccountDetails>({
        emailAddress: '',
        password: '',
    });
    const [sharedSecret, setSharedSecret] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [accountTypeAndServices, setAccountTypeAndServices] =
        useState<AccountTypeAndServicesPayload | null>(null);
    const [signUpFormData, setSignUpFormData] = useState<SignUpFormData | null>(null);

    const dispatch = useDispatch();
    const translations = usePageTranslations<SignInTranslation>(PageResourceKey.SIGN_IN);
    const authStatus = useSelector(selectAuthStatus);
    const cultureCode = useSelector(selectCultureCode);

    const resendEmailVerificationCode = (email: string, password: string) => {
        postEmailVerificationCode({
            username: email,
            password: password,
            bResendCode: true,
            emailVerificationCode: '',
        });
    };

    useEffect(() => {
        if (authStatus === 'signed_in') {
            navigate(`/${cultureCode}${AppPath.CHECKING_DETAILS}`);
        }
    }, [authStatus, cultureCode]);

    useEffect(() => {
        if (location?.state) {
            const initialState = location?.state as RegisterInitialState;
            if (initialState.stage === 'emailVerification') {
                setAccountDetails({
                    emailAddress: initialState.email,
                    password: initialState.password,
                });
                resendEmailVerificationCode(initialState.email, initialState.password);
                setRegistrationStep(RegisterStep.EMAIL_VERIFICATION);
            } else if (initialState.stage === 'tfa') {
                setAccountDetails({
                    emailAddress: initialState.email,
                    password: initialState.password,
                });
                setRegistrationStep(RegisterStep.SELECT_AUTH_TYPE);
            }
            dispatch(setAuthStatus('signed_out'));
        }
    }, [location?.state, dispatch]);

    const setRegistrationStepBack: React.Dispatch<React.SetStateAction<RegisterStep>> = (
        value
    ) => {
        const newStep =
            typeof value === 'function' ? value(RegisterStep.TFA_VERIFICATION) : value;
        if (newStep === RegisterStep.TFA_SETUP) {
            setTfaSetupSubStep('setup');
            setRegistrationStep(RegisterStep.TFA_SETUP);
        }
    };

    return (
        <Page isPublic={true}>
            <ErrorBoundary fallback={<ErrorFallback />}>
                <div
                    className={`RegistrationForm RegistrationFormWidth${
                        registrationStep === RegisterStep.ACCOUNT_TYPE_AND_SERVICES
                            ? ' RegistrationFormWide'
                            : ''
                    }`}
                >
                    {registrationStep === RegisterStep.SIGN_UP && (() => {
                        const signUpFormProps: SignUpFormProps = {
                            translations,
                            onComplete: () => setRegistrationStep(RegisterStep.ACCOUNT_TYPE_AND_SERVICES),
                            setAccountDetails,
                            setSignUpFormData,
                            signUpFormData
                        };
                        return <SignUpForm {...signUpFormProps} />;
                    })()}
                    {registrationStep === RegisterStep.ACCOUNT_TYPE_AND_SERVICES && (
                        <AccountTypeAndServices
                            translations={translations}
                            onComplete={() => setRegistrationStep(RegisterStep.EMAIL_VERIFICATION)}
                            accountTypeAndServices={accountTypeAndServices}
                            setAccountTypeAndServices={setAccountTypeAndServices}
                            signUpFormData={signUpFormData}
                            onBack={() => setRegistrationStep(RegisterStep.SIGN_UP)}
                        />
                    )}
                    {registrationStep === RegisterStep.EMAIL_VERIFICATION && (
                        <EmailVerification
                            translations={translations}
                            onComplete={() => setRegistrationStep(RegisterStep.SELECT_AUTH_TYPE)}
                            accountDetails={accountDetails}
                            onBack={() => setRegistrationStep(RegisterStep.ACCOUNT_TYPE_AND_SERVICES)}
                        />
                    )}
                    {registrationStep === RegisterStep.SELECT_AUTH_TYPE && (
                        <SelectAuthType
                            translations={translations}
                            onComplete={() => {
                                setTfaSetupSubStep('setup');
                                setRegistrationStep(RegisterStep.TFA_SETUP);
                            }}
                            authType={authType}
                            setAuthType={setAuthType}
                            onBack={() => setRegistrationStep(RegisterStep.EMAIL_VERIFICATION)}
                        />
                    )}
                    {registrationStep === RegisterStep.TFA_SETUP && tfaSetupSubStep === 'setup' && (
                        <TFASetup
                            translations={translations}
                            onComplete={() => {
                                setTfaSetupSubStep('select');
                                setRegistrationStep(RegisterStep.TFA_VERIFICATION);
                            }}
                            authType={authType}
                            setAuthType={setAuthType}
                            accountDetails={accountDetails}
                            setSharedSecret={setSharedSecret}
                            setPhoneNumber={setPhoneNumber}
                            onBack={() => {
                                setTfaSetupSubStep('select');
                                setRegistrationStep(RegisterStep.SELECT_AUTH_TYPE);
                            }}
                        />
                    )}
                    {registrationStep === RegisterStep.TFA_VERIFICATION && (
                        <TFAVerification
                            translations={translations}
                            onComplete={() => navigate(`/${cultureCode}${AppPath.SIGN_IN}`)}
                            authType={authType}
                            setAuthType={setAuthType}
                            accountDetails={accountDetails}
                            sharedSecret={sharedSecret}
                            phoneNumber={phoneNumber}
                            setRegistrationStep={setRegistrationStepBack}
                        />
                    )}
                </div>
            </ErrorBoundary>
        </Page>
    );
};
